<template>
    <admin-layout 
    :title="`View ${page?.label ?? '--'}`"
    show-back
    backUrl="/contents/pages/index">
        <div class="p-7">
            <template v-for="section in schema?.sections" :key="section.id">
                <FormSection>
                    <template #title>
                        {{ section?.label }}
                    </template>

                    <template v-if="section.description" #description>
                        {{ section.description }}
                    </template>

                    <template #form>
                        <div class="col-span-12 space-y-6">
                            <div v-for="item in section.items" :key="item.id">
                                <template v-if="item.type === 'htmleditor'">
                                    <CKEditor
                                        v-model="form[section.id + '_' + item.id]"
                                        label="Body"
                                        id="body"
                                        :config="{}"
                                    />
                                </template>
                                <template v-if="item.type === 'image'">
                                    <DropzoneInput 
                                        v-model:path="form[section.id + '_' + item.id]"
                                        v-model:file="form[section.id + '_' + item.id + '_file']"
                                        :label="item.label"
                                        :accept="'.png,.jpeg,.jpg,.webp'"
                                    />
                                </template>
                                <template v-if="item.type === 'text'">
                                    <text-input
                                        v-model="form[section.id + '_' + item.id]"
                                        :label="item.label"
                                        :id="`${section.id}_${item.id}`"
                                    />
                                    <p class="text-gray-400 text-sm mt-1">
                                        {{ item.description }}
                                    </p>
                                </template>
                                <template v-if="item.type === 'textarea'">
                                    <text-input
                                        v-model="form[section.id + '_' + item.id]"
                                        :label="item.label"
                                        textarea
                                        :id="`${section.id}_${item.id}`"
                                    />
                                    <p class="text-gray-400 text-sm mt-1">
                                        {{ item.description }}
                                    </p>
                                </template>
                                <template v-if="item.type === 'list_table'">
                                     <ListTable 
                                        :list="item"
                                        :items="item.items"
                                        :id="section.id + '_' + item.id"
                                        :form="form"
                                        @update-list="updateList"
                                    />
                                </template>
                            </div>
     
                        </div>
                    </template>
                </FormSection>
            </template>
            <div class="flex justify-end">
                <ButtonComponent
                    v-if="$hasPermission('update-content')" 
                    @click="showConfirmation = true"
                >
                    Save Changes
                </ButtonComponent>
            </div>
        </div>


        <PageLoader v-if="loader" />

        <ErrorModal
            width="w-[548px]"
            title="Error"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
            :errors="errors"
        >
            <template #content>
                {{ errorMsg }}
            </template>
        </ErrorModal>

        <SuccessModal
            :show="showSuccess"
            @cancel="showSuccess = false"
            @confirm="showSuccess = false"
            action-text="Confirm"
            :title="`${page?.label} Updated!`"
            :content="`${page?.label} has been updated successfully`"
        />

        <ConfirmationModal
            :show="showConfirmation"
            @cancel="showConfirmation = false"
            @confirm="showConfirmation = false; update() "
            cancel-text="Cancel"
            action-text="Confirm"
            :title="`Update ${page?.label}?`"
            :content="`Are you sure you wish to update this ${page?.label}?`"
        />
       
    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import FormSection from '@/components/FormSection.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import CKEditor from '@/components/inputs/CKEditor.vue';

import Pages from "@/classes/Pages";

import { PageInterface } from '@/interfaces/BaseInterface';

import { useRoute } from 'vue-router';
import { onMounted, ref } from 'vue';
import DropzoneInput from '@/components/inputs/DropzoneInput.vue';
import ListTable from '@/components/ListTable.vue';

const route = useRoute();

// Data
const loader    = ref(false);
const showError = ref(false);
const errorMsg  = ref(null);
const errors      = ref([])
const showSuccess = ref(false)
const showConfirmation = ref(false)

const item = ref<PageInterface>({
    id: Number(route.params.id),
    title: '',
    sections: [],
    content: '',
});

const page = ref(null);
const schema = ref(null);

const form = ref({});

// Methods
const edit = () => {
    loader.value = true;

    Pages.show(route.params.id)
        .then((response: any) => {
            loader.value = false;

            console.log(response.data)
            page.value = response.data.page;
            schema.value = response.data.schema;

            form.value = buildFormData(page.value, schema.value)

        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

const update = () => {
    loader.value = true;

    // Pages.update(item.value.id, {
    //     content: item.value.content
    // })
    //     .then(() => {
    //         loader.value = false;
    //         showSuccess.value = true;
    //     })
    //     .catch((err) => {
    //         loader.value = false;
    //         showError.value = true;
    //         errorMsg.value = err.response.data.message;

    //         if(err.response.status == 422) {
    //             errors.value = err.response.data.errors;
    //         }
            
    //         if(err.response.status == 401) {
    //             window.location.replace('/login');
    //         }
    //     })

    const formData = new FormData();

    Object.entries(form.value).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            // Skip null or undefined values
            return;
        }
        
        if (value instanceof File) {
            formData.append(key, value, value.name);
        } else if (Array.isArray(value)) {
            // if (value[0] instanceof File) {
            //     // Handle multiple file inputs
            //     value.forEach(file => {
            //         formData.append(key, file, file.name);
            //     });
            // } else {
            //     // Handle other array values
            //     value.forEach((item, index) => {
            //         formData.append(`${key}[${index}]`, item);
            //     });
            // }
            value.forEach((item, index) => {
                formData.append(`${key}[${index}]`, item);
            });
        } else {
            formData.append(key, value);
        }
    });


    // for (const [key, value] of formData.entries()) {
    //     if (value instanceof File) {
    //         console.log(key, ':', value.name, '(File)');
    //     } else {
    //         console.log(key, ':', value);
    //     }
    // }

    Pages.update(route.params.id, formData)
        .then(() => {
            loader.value = false;
            showSuccess.value = true;
        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;

            if(err.response.status == 422) {
                errors.value = err.response.data.errors;
            }
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

function updateList(event: any) {
    const keys = Object.keys(event.form);
    switch (event.action) {
      case "create":
        keys.forEach((key) => {
            if (!Array.isArray(form.value[key])) {
                form.value[key] = [];
            }
            form.value[key].push(event.form[key]);
        });
        break;
      case "edit":
        keys.forEach((key) => {
          form.value[key][event.selectedIndex] = event.form[key];
        });
        break;
      case "delete":
        keys.forEach((key) => {
          form.value[key].splice(event.selectedIndex, 1);
        });
        break;
      case "reorder":
        keys.forEach((key) => {
          form.value[key] = event.order.map((order) => {
            return form.value[key][order];
          });
        });
        break;
    }
  }

function buildFormData(page, schema) {
    const data = {
        title: page.title || null,
        description: page.description || null,
        keywords: page.keywords || null,
        robots_follow: page.robots_follow || true,
        robots_index: page.robots_index || true,
    };
    schema.sections.forEach((section) => {
        section.items.forEach((item) => {
        // get value from page
        const content = page.content;
        // console.log(content);
        if (item.type === "list_table") {
            // list items
            item.items.forEach((listItem) => {
                const contentId = `${section.id}_${item.id}`;
                const id = `${contentId}_${listItem.id}`;
                data[id] = [];
                if (listItem.type === "image") {
                    data[`${id}_file`] = [];
                }

                if (content && content[contentId] && content[contentId].length) {
                    content[contentId].forEach((contentItem) => {
                    data[id].push(contentItem[listItem.id]);
                    if (listItem.type === "image") {
                        data[`${id}_file`].push(null);
                    }
                    });
                }
            });
        } else {
            // normal items
            data[`${section.id}_${item.id}`] = content
                ? content[`${section.id}_${item.id}`] || null
                : null;
        }
        if (item.type === "image") {
            data[`${section.id}_${item.id}_file`] = null;
        }
        });
    });
    return data;
}


onMounted( () => {
    edit();
});

</script>